import React from 'react'

import {
  Checkbox,
  Button,
  Paper,
  FormGroup,
  FormControlLabel,
} from '@design/pt-ui'

import Select from '../../components/Select'

import { AdminFilterHook } from './Filter'

import { translateWithUserLocale } from '../../utils/i18n'

import Loader from '../../components/Loader'
import { AdvertiserUser } from '../../interfaces/entities/user'
import { Project } from '../../interfaces/entities/project'

export type UserPageProps = {
  adminFilter: AdminFilterHook
}

export default function UserPage(props: UserPageProps) {
  const projects = props.adminFilter.state.projects
  const advertiserUsers = props.adminFilter.state.advertiserUsers
  const selectedUser = props.adminFilter.state.selectedUser
  const copyFromUser = props.adminFilter.state.copyFromUser

  if (
    selectedUser === null ||
    projects === null ||
    props.adminFilter.state.updating
  ) {
    return <Loader variant="flex" />
  }

  const advertiserUserToOption = (au: AdvertiserUser) => {
    return {
      value: au.id,
      label: `${au.firstName} ${au.lastName}`,
    }
  }

  const projectToOption = (project: Project) => {
    return {
      value: project.id,
      label: project.name,
    }
  }

  const usersOptions = advertiserUsers
    .filter((au) => {
      return au.id !== selectedUser.id
    })
    .filter((au) => {
      return au.restrictedAccess
    })
    .map(advertiserUserToOption)

  const projectOptions = projects.map((p) => {
    return {
      value: p.id,
      label: p.name,
    }
  })

  const paperHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    backgroundColor: '#f6f6f6',
  }

  return (
    <div style={{ flexGrow: 1 }}>
      <h2
        style={{
          paddingBottom: 0,
          marginBottom: 0,
          paddingTop: 0,
          marginTop: 25,
        }}>
        {translateWithUserLocale('user.management_user_permissions')}
      </h2>

      {props.adminFilter.state.selectedProjects.length > 0 ||
      !selectedUser.restrictedAccess ? (
        ''
      ) : (
        <p
          style={{
            paddingBottom: 15,
            paddingTop: 0,
            marginTop: 0,
            fontWeight: 'bold',
          }}>
          {translateWithUserLocale('user.management_user_page_subtitle')}
        </p>
      )}

      <Paper sx={{ mt: '25px', mb: '25px' }}>
        <div style={paperHeaderStyle}>
          <h3>{translateWithUserLocale('user.management_user_cp_access')}</h3>
        </div>
        <div style={{ padding: 25 }}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!selectedUser.restrictedAccess}
                  name="fullA"
                  color="primary"
                />
              }
              label={translateWithUserLocale(
                'user.management_user_full_access',
              )}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="restrictedA"
                  color="primary"
                  checked={selectedUser.restrictedAccess}
                />
              }
              label={translateWithUserLocale(
                'user.management_user_restricted_access',
              )}
            />
          </FormGroup>
        </div>
      </Paper>

      {selectedUser.restrictedAccess ? (
        <>
          <Paper sx={{ mt: '25px', mb: '25px' }}>
            <div style={paperHeaderStyle}>
              <h3>
                {translateWithUserLocale('user.management_copy_permissions')}
              </h3>
            </div>
            <div style={{ padding: 25 }}>
              <p>
                {translateWithUserLocale(
                  'user.management_copy_permissions_from',
                )}{' '}
              </p>
              <Select
                isMulti={false}
                isClearable={true}
                className="cp-select-user"
                options={usersOptions}
                value={
                  copyFromUser
                    ? advertiserUserToOption(copyFromUser)
                    : undefined
                }
                onChange={props.adminFilter.handleCopyFromUserChange}
              />

              <Button
                onClick={props.adminFilter.copyPermissions}
                variant="contained"
                color="primary"
                className="cp-submit"
                sx={{ textTransform: 'none', mt: '25px' }}>
                {translateWithUserLocale('save')}
              </Button>
            </div>
          </Paper>
          <Paper sx={{ mt: '25px', mb: '25px' }}>
            <div style={paperHeaderStyle}>
              <h3>{translateWithUserLocale('projects')}</h3>
            </div>
            <div style={{ padding: 25 }}>
              <p>{translateWithUserLocale('user.management_user_projects')}</p>
              <Select
                className="cp-select-project"
                isMulti={true}
                isClearable={true}
                options={projectOptions}
                value={
                  props.adminFilter.state.selectedProjects
                    ? props.adminFilter.state.selectedProjects.map(
                        projectToOption,
                      )
                    : []
                }
                onChange={props.adminFilter.handleProjectChange}
              />

              <Button
                onClick={props.adminFilter.assignProjects}
                variant="contained"
                className="cp-submit"
                color="primary"
                sx={{ textTransform: 'none', mt: '25px' }}>
                {translateWithUserLocale('save')}
              </Button>
            </div>
          </Paper>
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
          }}>
          <h3>{translateWithUserLocale('user.management_user_page_info')}</h3>
        </div>
      )}
    </div>
  )
}
